import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteHandle } from '@/shared/lib';
import { Icon, LanguageSwitch, ThemeToggleButton, TileIconButton } from '@/shared/ui';
import { AccountMenu } from './account-menu';

interface AppHeaderProps {
  onMenuClick: () => void;
  /** Extra header actions (search, notifications) slotted in by the app layout. */
  actions?: ReactNode;
}

export function AppHeader({ onMenuClick, actions }: AppHeaderProps) {
  const { t } = useTranslation();
  const handle = useRouteHandle();

  return (
    <Box
      component="header"
      sx={(theme) => ({
        flex: 'none',
        position: 'relative',
        zIndex: 30,
        height: 74,
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        px: '22px',
        background: `color-mix(in srgb, ${theme.vars.palette.md.bg} 82%, transparent)`,
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${theme.vars.palette.md.border}`,
        [theme.breakpoints.down('sm')]: { px: 2, gap: '10px' },
      })}
    >
      <TileIconButton
        label={t('nav.openNavigation')}
        onClick={onMenuClick}
        sx={{ display: { lg: 'none' } }}
      >
        <Icon name="menu" />
      </TileIconButton>

      <Box sx={{ minWidth: 0, flex: '0 1 auto', mr: '6px' }}>
        <Typography
          variant="kicker"
          component="div"
          sx={{ fontSize: 9.5, letterSpacing: '.16em', display: { xs: 'none', sm: 'block' } }}
        >
          {t('app.brandKicker')}
        </Typography>
        <Typography variant="h1" noWrap sx={{ fontSize: { xs: 17, sm: 21 } }}>
          {handle ? t(`nav.items.${handle.page}`) : t('app.name')}
        </Typography>
      </Box>

      <Box sx={{ flex: 1 }} />
      {actions}
      <LanguageSwitch sx={{ flex: 'none', '@media (max-width:560px)': { display: 'none' } }} />
      <ThemeToggleButton />
      <AccountMenu />
    </Box>
  );
}
